import { useCallback, useRef } from 'react'
import type { FeedItem } from '../types/feedContract'
import {
  trackFeedItemViewed,
  trackVideoMuteToggled,
  type TrackedCardVariant,
} from '../services/feedTracking'

/**
 * Card-media handlers for the two video events RSI's Mixpanel expects. Kept out
 * of `CardMedia` so the player stays a presentation component: it reports that
 * a first play or a mute toggle happened, and this decides what that means.
 */
export function useCardMediaTracking(item: FeedItem, variant: TrackedCardVariant = 'full') {
  // Same reason as `usePostEngagement`: the surfaces rebuild their item objects
  // on every feed update, realtime pushes included. Reading the item through a
  // ref keeps both handlers stable, so a push does not re-create them and
  // through them `VideoPlayer`'s own play and mute callbacks.
  const latestItem = useRef(item)
  latestItem.current = item

  const onFirstPlay = useCallback(() => {
    trackFeedItemViewed(latestItem.current, variant)
  }, [variant])

  const onMuteToggle = useCallback((mutedAfterToggle: boolean) => {
    trackVideoMuteToggled(latestItem.current, mutedAfterToggle)
  }, [])

  // Returned unmemoized on purpose: callers spread this into CardMedia's props,
  // so the wrapper's identity is never compared — only the two callbacks are,
  // and useCallback already keeps those stable.
  return { onFirstPlay, onMuteToggle }
}
